import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { configureApi } from '../api/client';
import { clearSession, loadSession, saveSession, type Session } from './session';

interface AuthContextValue {
  session: Session | null;
  signIn: (session: Session) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const tokenRef = useRef<string | null>(session?.accessToken ?? null);

  const signIn = useCallback((next: Session) => {
    tokenRef.current = next.accessToken;
    saveSession(next);
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    tokenRef.current = null;
    clearSession();
    setSession(null);
    // Never show the previous user's cached data to the next person on this browser.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    configureApi({ getToken: () => tokenRef.current, onUnauthorized: signOut });
  }, [signOut]);

  // Another tab signing out (or in) must be reflected here.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === 'movo.session.v1') {
        const next = loadSession();
        tokenRef.current = next?.accessToken ?? null;
        setSession(next);
        if (!next) {
          queryClient.clear();
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queryClient]);

  const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}

/** For screens that only render when signed in (everything behind <AuthGuard>). */
export function useSession(): Session {
  const { session } = useAuth();
  if (!session) {
    throw new Error('useSession called without a signed-in user');
  }
  return session;
}
